import React, { useMemo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader2, Send } from "lucide-react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  useGetMyTimesheetsQuery,
  useSubmitWeekMutation,
  useDeleteTimesheetMutation,
  useCreateTimesheetMutation,
  useUpdateTimesheetMutation,
  useSubmitTimesheetMutation,
} from "../../store/apis/timesheet.api";
import { useGetWorkingHoursConfigQuery } from "../../store/apis/organization.api";
import { useAppSelector } from "../../store/hooks";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

import {
  computeTaskHours,
  computeWeekHours,
  computeWeekProjects,
  countWorkingDays,
  DAY_LABELS,
  formatWeekSubtitle,
  getWeekRange,
  taskToDraft,
  toEntryDateKey,
  toWeekDateKey,
  validateWeekSubmission,
  type DraftTask,
  type DayEntryMap,
} from "../../utils/timesheetWeek";
import { isWeekOffDate } from "../../utils/weekOff";
import type { ITimesheet } from "../../types/timesheet.api.types";

import { PageHeader, ConfirmationModal, Button } from "../common";
import WeeklySummaryCard from "./WeeklySummaryCard";
import DayTimesheetCard from "./DayTimesheetCard";
import WeekendCard from "./WeekendCard";
import TaskModal from "./TaskModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const WEEK_LIMIT = 100;

const WeekTimesheetView: React.FC = () => {
  const userTimezone = dayjs.tz.guess();
  const canManage = useHasPermission(PERMISSIONS.MY_TIMESHEETS_MANAGE);

  const { user, weekOffConfig } = useAppSelector((state) => state.auth);
  const { data: workingHoursConfig } = useGetWorkingHoursConfigQuery(
    user?.organizationId || "",
    { skip: !user?.organizationId },
  );

  const [weekOffset, setWeekOffset] = useState(0);
  const range = useMemo(() => getWeekRange(weekOffset), [weekOffset]);
  const startKey = toWeekDateKey(range.start);
  const endKey = toWeekDateKey(range.end);

  // Figma layout: Mon–Fri as the 5 weekday cards, Sat + Sun grouped under "Weekend".
  const weekdayDays = useMemo(
    () => range.days.filter((d) => d.getDay() >= 1 && d.getDay() <= 5),
    [range],
  );
  const weekendDays = useMemo(() => {
    const sat = range.days.find((d) => d.getDay() === 6);
    const sun = range.days.find((d) => d.getDay() === 0);
    return [sat, sun].filter(Boolean) as Date[];
  }, [range]);
  const fullDayHours = useMemo(
    () => Number(workingHoursConfig?.fullDayHours) || 7,
    [workingHoursConfig],
  );

  const [editorTasks, setEditorTasks] = useState<Record<string, DraftTask[]>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dayToDelete, setDayToDelete] = useState<{
    key: string;
    id: string;
    label: string;
  } | null>(null);
  const [activeTaskModal, setActiveTaskModal] = useState<{
    day: Date;
    index: number | "new";
  } | null>(null);

  const { data: timesheetRes, isLoading } = useGetMyTimesheetsQuery({
    startDate: startKey,
    endDate: endKey,
    page: 1,
    limit: WEEK_LIMIT,
  });
  const entries: ITimesheet[] = timesheetRes?.data ?? [];

  const entryByDate = useMemo<DayEntryMap>(() => {
    const map: DayEntryMap = {};
    for (const e of entries) {
      const key = toEntryDateKey(e.date);
      if (key) map[key] = e;
    }
    return map;
  }, [entries]);

  const entriesFingerprint = entries
    .map((e) => `${e.id}:${e.status}:${e.totalHours}`)
    .join("|");

  // Sync editor tasks & auto-select draft timesheet IDs
  useEffect(() => {
    const next: Record<string, DraftTask[]> = {};
    const selectableDraftIds: string[] = [];

    for (const day of range.days) {
      const key = toWeekDateKey(day);
      const entry = entryByDate[key];
      if (!entry || entry.status === "draft") {
        const loadedTasks = entry ? entry.tasks.map((t) => taskToDraft(t, userTimezone)) : [];
        next[key] = loadedTasks;
        if (entry?.id && loadedTasks.length > 0) {
          selectableDraftIds.push(entry.id);
        }
      } else {
        next[key] = [];
      }
    }
    setEditorTasks(next);
    setSelectedIds(new Set(selectableDraftIds));
  }, [startKey, entriesFingerprint]);

  const [createTimesheet] = useCreateTimesheetMutation();
  const [updateTimesheet] = useUpdateTimesheetMutation();
  const [submitTimesheet] = useSubmitTimesheetMutation();
  const [submitWeek, { isLoading: isSubmitting }] = useSubmitWeekMutation();
  const [deleteTimesheet, { isLoading: isDeleting }] =
    useDeleteTimesheetMutation();

  const handleSingleDaySubmit = async (id: string, dayLabel: string) => {
    try {
      await submitTimesheet(id).unwrap();
      toast.success(`${dayLabel} timesheet submitted successfully`);
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        `Failed to submit ${dayLabel} timesheet`;
      toast.error(message);
    }
  };

  const toggleSelectId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const targetHours = useMemo(() => {
    const workingDays = countWorkingDays(range.days, weekOffConfig);
    return Math.round(fullDayHours * workingDays * 100) / 100;
  }, [range, fullDayHours, weekOffConfig]);

  const weekTotal = useMemo(
    () => computeWeekHours(range.days, editorTasks, entryByDate),
    [range, editorTasks, entryByDate],
  );
  const weekProjects = useMemo(
    () => computeWeekProjects(range.days, editorTasks, entryByDate),
    [range, editorTasks, entryByDate],
  );

  const handleSaveTask = async (task: DraftTask) => {
    if (!activeTaskModal) return;
    const day = activeTaskModal.day;
    const key = toWeekDateKey(day);
    const currentTasks = editorTasks[key] || [];
    let nextTasks: DraftTask[];

    if (activeTaskModal.index === "new") {
      nextTasks = [...currentTasks, task];
    } else {
      nextTasks = currentTasks.map((t, i) => (i === activeTaskModal.index ? task : t));
    }

    const payloadTasks = nextTasks.map((t) => ({
      name: t.name.trim(),
      project: t.project.trim() || undefined,
      startTime: dayjs(`${key} ${t.startTime}`).tz(userTimezone).utc().toISOString(),
      endTime: dayjs(`${key} ${t.endTime}`).tz(userTimezone).utc().toISOString(),
      hours: computeTaskHours(t),
      description: t.description.trim() || undefined,
    }));
    const totalHours = Number(
      payloadTasks.reduce((sum, x) => sum + x.hours, 0).toFixed(2),
    );

    const existingEntry = entryByDate[key];

    try {
      if (existingEntry && existingEntry.id) {
        await updateTimesheet({
          id: existingEntry.id,
          body: { date: key, tasks: payloadTasks },
        }).unwrap();
        toast.success("Timesheet updated");
      } else {
        await createTimesheet({
          date: key,
          tasks: payloadTasks,
          totalHours,
        }).unwrap();
        toast.success("Timesheet saved as draft");
      }
      setEditorTasks((prev) => ({ ...prev, [key]: nextTasks }));
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        "Failed to save timesheet draft";
      toast.error(message);
    } finally {
      setActiveTaskModal(null);
    }
  };

  const handleTasksChange = async (key: string, nextTasks: DraftTask[]) => {
    setEditorTasks((prev) => ({ ...prev, [key]: nextTasks }));
    const existingEntry = entryByDate[key];
    if (!existingEntry || !existingEntry.id) return;

    if (nextTasks.length === 0) {
      try {
        await deleteTimesheet(existingEntry.id).unwrap();
        toast.success("Timesheet deleted");
      } catch {
        toast.error("Failed to update timesheet");
      }
      return;
    }

    const payloadTasks = nextTasks.map((t) => ({
      name: t.name.trim(),
      project: t.project.trim() || undefined,
      startTime: dayjs(`${key} ${t.startTime}`).tz(userTimezone).utc().toISOString(),
      endTime: dayjs(`${key} ${t.endTime}`).tz(userTimezone).utc().toISOString(),
      hours: computeTaskHours(t),
      description: t.description.trim() || undefined,
    }));

    try {
      await updateTimesheet({
        id: existingEntry.id,
        body: { date: key, tasks: payloadTasks },
      }).unwrap();
    } catch {
      toast.error("Failed to update timesheet");
    }
  };

  const handleSubmitWeek = async () => {
    const validation = validateWeekSubmission(
      range.days,
      entryByDate,
      weekOffConfig,
      fullDayHours,
    );

    if (!validation.isValid) {
      if (validation.errorMessage) toast.error(validation.errorMessage);
      return;
    }

    try {
      const res = await submitWeek({
        timesheetIds: validation.timesheetIdsToSubmit,
        startDate: startKey,
        endDate: endKey,
      }).unwrap();
      const parts: string[] = [];
      if (res?.submitted) parts.push(`${res.submitted} submitted`);
      if (res?.failed) parts.push(`${res.failed} failed`);
      toast.success(
        parts.length ? `Timesheets submitted: ${parts.join(", ")}` : "Timesheets submitted",
      );
      if (res?.failed) {
        toast.error("Some selected timesheets could not be submitted.");
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        "Failed to submit selected timesheets";
      toast.error(message);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!dayToDelete) return;
    try {
      await deleteTimesheet(dayToDelete.id).unwrap();
      toast.success("Timesheet deleted");
    } catch {
      toast.error("Failed to delete timesheet");
    }
    setDayToDelete(null);
  };

  return (
    <div className="w-full">
      <PageHeader
        breadcrumbs={[
          { label: "Home", path: "/dashboard" },
          { label: "My Timesheets" },
        ]}
        title="My Timesheets"
        description={formatWeekSubtitle(range)}
        className="mb-5 sm:mb-6"
        actions={
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o - 1)}
                aria-label="Previous week"
                className="w-10 h-10 grid place-items-center border border-[var(--color-layout-dropdown-badge-border)] bg-white rounded-[11px] text-text-secondary hover:border-primary-700 hover:text-primary-700 transition-all"
              >
                <ChevronLeft className="w-[17px] h-[17px]" />
              </button>
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o + 1)}
                aria-label="Next week"
                className="w-10 h-10 grid place-items-center border border-[var(--color-layout-dropdown-badge-border)] bg-white rounded-[11px] text-text-secondary hover:border-primary-700 hover:text-primary-700 transition-all"
              >
                <ChevronRight className="w-[17px] h-[17px]" />
              </button>
            </div>
            {canManage && (
              <Button
                appearance="primary"
                onClick={handleSubmitWeek}
                loading={isSubmitting}
                icon={<Send className="w-4 h-4" />}
              >
                Submit Week
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-5 sm:mb-6">
        <WeeklySummaryCard
          totalHours={weekTotal}
          targetHours={targetHours}
          projects={weekProjects}
          loading={isLoading}
        />
      </div>

      {/* Weekday Cards (Mon–Fri) */}
      <div className="mb-5 sm:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5 items-start">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="w-full h-[240px] rounded-[18px] bg-white border border-border-secondary animate-pulse shadow-soft"
              />
            ))
          : weekdayDays.map((day) => {
              const key = toWeekDateKey(day);
              const entry = entryByDate[key];
              const locked = !!entry && entry.status !== "draft";
              const isWeekOff = isWeekOffDate(day, weekOffConfig);

              return (
                <DayTimesheetCard
                  key={key}
                  day={day}
                  entry={entry}
                  mode={locked ? "locked" : "editable"}
                  tasks={editorTasks[key] || []}
                  isSubmitting={isSubmitting}
                  isWeekOff={isWeekOff}
                  dayTargetHours={fullDayHours}
                  onTasksChange={(nextTasks) => handleTasksChange(key, nextTasks)}
                  onAddTask={
                    locked
                      ? undefined
                      : () => setActiveTaskModal({ day, index: "new" })
                  }
                  onEditTask={
                    locked
                      ? undefined
                      : (taskIndex) =>
                          setActiveTaskModal({ day, index: taskIndex })
                  }
                  onDeleteDay={
                    locked
                      ? undefined
                      : entry
                        ? () =>
                            setDayToDelete({
                              key,
                              id: entry.id,
                              label: DAY_LABELS[day.getDay()],
                            })
                        : undefined
                  }
                  onSubmitSingleDay={
                    locked || !entry?.id
                      ? undefined
                      : () => handleSingleDaySubmit(entry.id, DAY_LABELS[day.getDay()])
                  }
                />
              );
            })}
        </div>
      </div>

      {/* Weekend (Sat + Sun) */}
      <div className="mb-5 sm:mb-6">
        <div className="text-caption font-bold uppercase tracking-[0.08em] text-text-tertiary mb-3">
          Weekend
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
          {isLoading
            ? Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="w-full h-[74px] rounded-[16px] bg-white border border-border-secondary animate-pulse shadow-soft"
                />
              ))
            : weekendDays.map((day) => {
                const key = toWeekDateKey(day);
                const entry = entryByDate[key];
                const locked = !!entry && entry.status !== "draft";
                const isWeekOff = isWeekOffDate(day, weekOffConfig);

                return (
                  <WeekendCard
                    key={key}
                    day={day}
                    entry={entry}
                    tasks={editorTasks[key] || []}
                    mode={locked ? "locked" : "editable"}
                    isSubmitting={isSubmitting}
                    isWeekOff={isWeekOff}
                    dayTargetHours={fullDayHours}
                    onTasksChange={(nextTasks) => handleTasksChange(key, nextTasks)}
                    onSubmitSingleDay={
                      locked || !entry?.id
                        ? undefined
                        : () => handleSingleDaySubmit(entry.id, DAY_LABELS[day.getDay()])
                    }
                    onAddTask={
                      locked
                        ? undefined
                        : () => setActiveTaskModal({ day, index: "new" })
                    }
                    onEditTask={
                      locked
                        ? undefined
                        : (taskIndex) =>
                            setActiveTaskModal({ day, index: taskIndex })
                    }
                  />
                );
              })}
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!dayToDelete}
        onClose={() => setDayToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Timesheet"
        message={`Are you sure you want to delete the ${dayToDelete?.label} timesheet? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      {activeTaskModal && (
        <TaskModal
          open={!!activeTaskModal}
          day={activeTaskModal.day}
          initial={
            typeof activeTaskModal.index === "number"
              ? (editorTasks[toWeekDateKey(activeTaskModal.day)] || [])[
                  activeTaskModal.index
                ] ?? null
              : null
          }
          isSubmitting={isSubmitting}
          onClose={() => setActiveTaskModal(null)}
          onSave={handleSaveTask}
        />
      )}
    </div>
  );
};

export default WeekTimesheetView;
